// client/src/pages/Teacher/TeacherDashboardPage.js
//
// Web counterpart of apps/mobile/src/screens/teacher/TeacherHomeScreen.js.
//
// The dashboard was built for mobile first and had no web equivalent, so a
// teacher signing in on a desktop landed on whichever page happened to be first
// in their menu. Same data, same derivations, same decisions about what is
// worth showing — only the presentation differs, because a desktop has room for
// the charts side by side where a phone stacks them.
//
// Two of those decisions are deliberate and worth keeping:
//
//   * There is no single "average" figure. Averaging marks across unrelated
//     classes and subjects produces a number that means nothing; what a teacher
//     opens the app to confirm is what they are responsible for and what still
//     needs grading.
//
//   * Departments are de-duplicated by id. /api/users/me returns three
//     overlapping lists — subjectHeadOf, subjectGroupsHeaded and
//     departmentHeadOf — and concatenating them prints the same department once
//     per subject it contains, so a head of six subjects saw six near-identical
//     entries.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  Grid, Paper, Stack, Typography, useTheme,
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import ClassIcon from '@mui/icons-material/Class';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { fetchJson } from '../../utils/fetchJson';
import useScopeAutoRefresh from '../../hooks/useScopeAutoRefresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { assignmentSections, subjectLevelLabel } from '../../utils/marksSheet.generated';
import { usePhase } from '../../contexts/PhaseContext';

const DEFAULT_PASS_MARK = 10;

/** Mean of the scores that are actually numbers, or null when there are none. */
const averageFromScores = (scores) => {
  const nums = (scores || [])
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const fmt = (v) => (v == null ? '—' : (Math.round(v * 100) / 100).toFixed(2));

export default function TeacherDashboardPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  // The phase decides whether the subject list is worth showing at all —
  // see showsOwnSubjects below.
  const { activePhase } = usePhase();
  const [classes, setClasses] = useState([]);
  const [mySubjects, setMySubjects] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [marks, setMarks] = useState([]);
  const [discipline, setDiscipline] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Class-master widgets are shown when the server says so. The privilege is
  // derived per request from what the user actually heads, so asking /users/me
  // keeps this page consistent with what the permission system granted rather
  // than second-guessing it from a role name.
  const isClassMaster = useMemo(
    () => (Array.isArray(me?.privileges) ? me.privileges : []).includes('ClassMaster'),
    [me]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const profile = await fetchJson('/api/users/me').catch(() => null);
      setMe(profile);

      const cls = await fetchJson('/api/teacher/classes').catch(() => null);
      const list = Array.isArray(cls) ? cls : (cls?.classes || cls?.data || []);
      setClasses(list);

      const classMaster = (Array.isArray(profile?.privileges) ? profile.privileges : [])
        .includes('ClassMaster');

      const ids = list.map((c) => c.id).filter(Boolean);

      // Each widget is allowed to fail on its own. A dashboard that blanks
      // because one endpoint is down is worse than a partial one.
      const [evals, markRes, subj, disc, uc] = await Promise.allSettled([
        // EVERY CLASS, NOT JUST THE FIRST. This asked for `list[0].id` alone, so
        // a teacher with four classes saw one class's worth of everything and no
        // indication the other three were missing. The phone was fixed months
        // ago; this copy was not, which is the disparity itself.
        Promise.allSettled(ids.map((id) =>
          fetchJson(`/api/academics/evaluations/class/${encodeURIComponent(id)}/evaluations`))),
        // THE MARKS THEMSELVES. /evaluations returns the LIST of a class's
        // evaluations and carries no scores at all, so reading `e.marks` off it
        // — which this page did — was always an empty array: every point on the
        // trend was blank and every evaluation reported "no marks entered",
        // whether or not the teacher had entered any.
        Promise.allSettled(ids.map((id) =>
          fetchJson(`/api/marks?classId=${encodeURIComponent(id)}`))),
        // WHAT THEY TEACH, from the ALLOCATION. Not getClassSubjects, which
        // derives from the timetable — anyone dropped onto a slot appears to
        // teach that subject whether or not they were ever allocated it. The
        // phone has read this since it was fixed there; the web never asked.
        fetchJson('/teacher/my-subjects').catch(() => []),
        classMaster
          ? fetchJson('/api/class-discipline/summary/sequence?sequenceNo=1')
          : Promise.resolve(null),
        fetchJson('/api/messages/unread-count'),
      ]);

      const settled = (r) => (r.status === 'fulfilled' && Array.isArray(r.value)
        ? r.value.filter((x) => x.status === 'fulfilled').map((x) => x.value)
        : []);

      // De-duplicated across classes: the six sequences are the same six
      // whichever class they are read through.
      const evalSeen = new Map();
      for (const raw of settled(evals)) {
        const arr = Array.isArray(raw) ? raw : (raw?.evaluations || raw?.data || []);
        for (const ev of arr) {
          const key = String(ev.sequenceNo ?? ev.number ?? ev.name ?? ev.id);
          if (!evalSeen.has(key)) evalSeen.set(key, ev);
        }
      }
      setEvaluations(Array.from(evalSeen.values()));

      setMarks(settled(markRes).flatMap((raw) => (
        Array.isArray(raw) ? raw : (raw?.marks || raw?.rows || raw?.data || [])
      )));

      if (subj.status === 'fulfilled') {
        const v = subj.value;
        setMySubjects(Array.isArray(v) ? v : (v?.subjects || v?.data || []));
      }
      if (disc.status === 'fulfilled' && disc.value) {
        const d = disc.value;
        setDiscipline(Array.isArray(d?.rows) ? d.rows : (d?.summary || d?.data || []));
      }
      if (uc.status === 'fulfilled') {
        const v = uc.value;
        setUnread(Number(v?.count ?? v?.unread ?? v) || 0);
      }
    } catch (err) {
      setError(err?.userMessage || err?.message || t('common.genericError', 'Something went wrong.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // AND AGAIN WHEN THE SCOPE MOVES. Every figure on this dashboard — the
  // classes, the subjects, the marks still to enter — is scoped to a year and a
  // phase, and both are switchable from the top bar. Without this the numbers
  // simply stay as they were, which reads as "nothing to do this year" rather
  // than as a screen that has not been asked again.
  useScopeAutoRefresh([load], [load]);

  /**
   * ONE POINT PER EVALUATION, AVERAGED ACROSS EVERY CLASS THIS TEACHER TEACHES.
   *
   * Byte-for-byte the phone's `series`, and it had to become so: this page was
   * mapping over `evaluations` and reading `e.marks`, a field that endpoint does
   * not return, so every point was blank and every row said "no marks entered".
   *
   * Keyed on the sequence NUMBER where there is one — each class holds its own
   * Evaluation row for the same sequence, so keying on the row id would draw
   * Sequence 2 once per class.
   */
  const series = useMemo(() => {
    const bucket = new Map();
    const keyOf = (ev) => String(
      ev?.sequenceNumber ?? ev?.sequenceNo ?? ev?.number ?? ev?.name ?? ev?.id ?? ''
    );

    for (const ev of evaluations) {
      const k = keyOf(ev);
      if (!k) continue;
      bucket.set(k, {
        order: Number(ev.sequenceNo ?? ev.number) || 999,
        label: ev.sequenceNo ? `S${ev.sequenceNo}` : String(ev.name || k).slice(0, 4),
        name: ev.name || ev.title || `#${ev.id}`,
        scores: [],
        // MARKS ARE FILED PER SUBJECT, so "Sequence 2: filled" was answering a
        // question nobody asked. A teacher with three subjects needs to know
        // WHICH of them are done.
        subjects: new Set(),
      });
    }

    for (const m of marks) {
      const ev = m?.Evaluation || m?.evaluation || {};
      const k = keyOf(ev) || String(m?.evaluationId ?? '');
      if (!k) continue;
      if (!bucket.has(k)) {
        bucket.set(k, {
          order: Number(ev.sequenceNumber ?? ev.sequenceNo) || 999,
          label: ev.sequenceNumber ? `S${ev.sequenceNumber}` : String(ev.name || k).slice(0, 4),
          name: ev.name || `#${k}`,
          scores: [],
          subjects: new Set(),
        });
      }
      const score = Number(m?.score);
      if (Number.isFinite(score)) {
        bucket.get(k).scores.push(score);
        const subjectName = m?.Subject?.name || m?.subject?.name || m?.subjectName
          || (m?.subjectId != null ? `#${m.subjectId}` : '');
        if (subjectName) bucket.get(k).subjects.add(subjectName);
      }
    }

    return Array.from(bucket.values())
      .sort((a, b) => a.order - b.order)
      .map((b) => ({
        label: b.label,
        name: b.name,
        value: averageFromScores(b.scores),
        filled: b.scores.length > 0,
        count: b.scores.length,
        subjects: Array.from(b.subjects).sort(),
      }));
  }, [evaluations, marks]);

  const pending = useMemo(() => series.filter((s) => !s.filled), [series]);

  const classMasterOf = Array.isArray(me?.classMasterOf) ? me.classMasterOf : [];

  const headedDepartments = useMemo(() => {
    const seen = new Map();
    for (const d of (Array.isArray(me?.departmentHeadOf) ? me.departmentHeadOf : [])) {
      const key = String(d.id ?? d.name);
      if (!seen.has(key)) seen.set(key, d);
    }
    return Array.from(seen.values());
  }, [me]);

  const disciplineSeries = useMemo(() => {
    if (!discipline.length) return [];
    const warn = discipline.reduce((s, r) => s + (r.manual?.warnings || 0), 0);
    const susp = discipline.reduce((s, r) => s + (r.manual?.suspension || 0), 0);
    const late = discipline.reduce((s, r) => s + (r.lateMinutes || 0), 0);
    const abs = discipline.reduce((s, r) => s + (r.unjustifiedAbsenceMinutes || 0), 0);
    return [
      { label: t('classDisciplinePage.warnings', 'Warnings'), value: warn },
      { label: t('classDisciplinePage.suspension', 'Suspensions'), value: susp },
      // Minutes are converted to hours; a bar of "480" against a bar of "3"
      // renders the smaller one invisible.
      { label: t('classDisciplinePage.lateMinutes', 'Late (hrs)'), value: Math.round(late / 60) },
      { label: t('classDisciplinePage.absentMinutes', 'Absent (hrs)'), value: Math.round(abs / 60) },
    ];
  }, [discipline, t]);

  const passColour = theme.palette.mode === 'dark' ? '#7BD389' : '#166534';
  const scoreColour = (v) => {
    if (v == null) return theme.palette.text.disabled;
    return v >= DEFAULT_PASS_MARK ? passColour : theme.palette.error.main;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  // WHOSE SUBJECTS ARE WORTH LISTING.
  //
  // Not a nursery or primary CLASS TEACHER: they take every subject their class
  // is allocated, so the list is the class's syllabus rather than anything
  // personal — it says nothing they do not know and pushes down what changes day
  // to day. A nur/pri teacher who is NOT a class teacher is a specialist, and
  // their two or three subjects are exactly what distinguishes their week.
  //
  // `isClassTeacher` comes from the classes themselves, not from a page
  // permission: /api/teacher/classes flags the ones matched because the teacher
  // RUNS them, and a permission only says what someone may open.
  const isClassTeacher = classes.some((c) => c.isClassTeacher);
  // WHICH ROWS THIS CARD SHOWS — decided in @ameca/core so the phone's
  // dashboard cannot reach a different answer about the same teacher.
  const sections = assignmentSections({ phaseCode: activePhase, isClassTeacher });
  const showSubjects = sections.showSubjects;

  const ChipRow = ({ label, items, icon }) => (
    <>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" mt={0.5} mb={1}>
        {items.length === 0
          ? <Typography variant="body2" color="text.secondary">—</Typography>
          : items.map((c) => (
            <Chip key={c.id ?? c.name} size="small" icon={icon} label={c.name} />
          ))}
      </Stack>
    </>
  );

  return (
    <Box p={{ xs: 2, md: 3 }}>
      {Boolean(error) && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Typography variant="h6" fontWeight={700} gutterBottom>
        {me?.name || ''}
      </Typography>

      <Grid container spacing={2}>
        {/* What this teacher is responsible for. */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t('teacherHome.assignments', 'My assignments')}
              </Typography>
              {/* "Class teacher of" in nursery/primary when they run the
                  class, "Teaching Class(es)" otherwise. The school's own word
                  for the job, not a second row saying the same thing. */}
              <ChipRow
                label={sections.classesLabel === 'classTeacherOf'
                  ? t('teacherHome.classTeacherOf', 'Class teacher of')
                  : t('teacherHome.classesTaught', 'Teaching Class(es)')}
                items={classes}
                icon={<ClassIcon />}
              />
              {showSubjects && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ChipRow
                    label={t('teacherHome.subjectsTaught', 'Your subject(s)')}
                    // THE LEVEL IS PART OF WHICH SUBJECT THIS IS. This school
                    // runs Mathematics, Physics, ICT, Philosophy and Physical
                    // Education at two levels each, as separate subjects with
                    // separate teachers, so a bare "Mathematics" does not say
                    // which one the teacher holds.
                    items={mySubjects.map((sub) => {
                      const level = subjectLevelLabel(sub.level);
                      const name = sub.name || sub.subjectName || `#${sub.id}`;
                      return {
                        id: sub.id ?? sub.subjectId ?? name,
                        name: level ? `${name} · ${level}` : name,
                      };
                    })}
                    icon={<MenuBookIcon />}
                  />
                </>
              )}
              {/* SECONDARY ONLY. "Class master" is a secondary word — in
                  nursery/primary the same column means CLASS TEACHER, and the
                  row above has already said so. */}
              {sections.showClassMasterOf && classMasterOf.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ChipRow
                    label={t('teacherHome.classMasterOf', 'Class master of')}
                    items={classMasterOf}
                    icon={<StarOutlineIcon />}
                  />
                </>
              )}
              {sections.showHeadOfDepartment && headedDepartments.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ChipRow
                    label={t('teacherHome.headOfDepartment', 'Head of department')}
                    items={headedDepartments}
                    icon={<ApartmentIcon />}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('teacherHome.graded', 'Evaluations graded')}
                </Typography>
                <Typography variant="h5" fontWeight={700}
                  color={pending.length ? 'warning.main' : passColour}>
                  {series.length - pending.length}/{series.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('messagesPage.inbox', 'Inbox')}
                </Typography>
                <Typography variant="h5" fontWeight={700}
                  color={unread ? 'warning.main' : 'text.primary'}>
                  {unread}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('teacherHome.performanceTrend', 'Performance across evaluations')}
                  </Typography>
                  {series.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('classPerformance.empty', 'No evaluations yet')}
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={series}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis domain={[0, 20]} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Line type="monotone" dataKey="value" stroke={theme.palette.primary.main} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Which evaluations still need marks — the actionable half. */}
        <Grid item xs={12} md={isClassMaster ? 6 : 12}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">
                  {t('teacherHome.gradingStatus', 'Evaluation grading status')}
                </Typography>
                {series.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {series.length - pending.length}/{series.length}
                  </Typography>
                )}
              </Stack>

              {series.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {t('classPerformance.empty', 'No evaluations yet')}
                </Typography>
              ) : series.map((s, i) => (
                <React.Fragment key={i}>
                  <Stack direction="row" alignItems="center" gap={1.5} py={1}>
                    {s.filled
                      ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: passColour }} />
                      : <PendingActionsIcon fontSize="small" color="error" />}
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.filled
                          ? t('teacherHome.marksEntered', { count: s.count, defaultValue: '{{count}} marks entered' })
                          : t('teacherHome.notFilled', 'No marks entered yet')}
                      </Typography>
                      {/* WHICH SUBJECTS. Named rather than counted: "2 subjects"
                          still leaves the teacher to work out which two, and the
                          point of this list is to be actionable without opening
                          anything. */}
                      {s.subjects.length > 0 && (
                        <Typography
                          variant="caption"
                          display="block"
                          noWrap
                          sx={{ color: 'primary.main', fontWeight: 600 }}
                        >
                          {s.subjects.join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: scoreColour(s.value) }}>
                      {fmt(s.value)}
                    </Typography>
                  </Stack>
                  <Divider />
                </React.Fragment>
              ))}

              {pending.length > 0 && (
                <Button
                  variant="outlined" startIcon={<EditNoteIcon />} sx={{ mt: 2 }}
                  onClick={() => navigate('/teacher/student-grading')}
                >
                  {t('teacherMarks.pageTitle', 'Enter marks')}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Class-master widgets, shown only when the server grants the privilege. */}
        {isClassMaster && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('teacherHome.classPerformance', 'Class performance')}
                </Typography>
                {series.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('classPerformance.empty', 'No evaluations yet')}
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={series}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis domain={[0, 20]} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Bar dataKey="value">
                        {series.map((s, i) => <Cell key={i} fill={scoreColour(s.value)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <Button size="small" onClick={() => navigate('/classmaster/class-academic-performance')}>
                  {t('common.viewAll', 'View all')}
                </Button>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  {t('teacherHome.classDiscipline', 'Class discipline')}
                </Typography>
                {disciplineSeries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('classDisciplinePage.empty', 'No records yet')}
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={disciplineSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      {/* Scaled to the data: counts of warnings have no natural
                          ceiling the way a mark out of 20 does. */}
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <Button size="small" onClick={() => navigate('/classmaster/class-discipline')}>
                  {t('common.viewAll', 'View all')}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
